import type { Material } from "@babylonjs/core/Materials/material";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Scene } from "@babylonjs/core/scene";
import {
  PIT,
  START_LINE_X,
  TRACK,
  TRUCK_ARENA_BOUNDS,
  TRUCK_BARRIERS,
  TRUCK_CHECKPOINTS,
  TRUCK_FLEET,
  TRUCK_MUD,
  TRUCK_RAMPS,
  baySpan,
  rampHeightAt,
  type TruckBarrier,
  type TruckRamp,
} from "@townsy/world";
import { makeDynamicTexture, mulberry32 } from "../../scene/core/materials";
import { PartBatch, applyPart, box, polyMesh, setThinInstances, thinInstanceMatrices, type Xyz } from "../../scene/core/meshKit";
import { DIRT_RECT, DIRT_TEXTURE, paintDirt } from "./dirtTexture";
import { TRUCK_PAINTS } from "./palette";
import { numberTexture } from "./truckModel";

const CONCRETE = "#C9C4BA";
const HAZARD_Y = "#F2C230";
const HAZARD_K = "#1E1E1E";
const PLANK = "#B98450";
const PLANK_ALT = "#A87445";
const RAMP_SIDE = "#7A5230";

export interface ArenaStatic {
  /** Shadow casters (static, built once). */
  casters: Mesh[];
  /** The five start lights on the gantry (left to right seen from the grid). */
  startLights: Mesh[];
  dispose(): void;
}

function axes(h: number) {
  return { fx: Math.sin(h), fz: Math.cos(h), rx: Math.cos(h), rz: -Math.sin(h) };
}

/** A box covering a barrier's oriented box (a little larger so the room's 0.9 m fallback fixture never shows). */
function barrierBox(b: PartBatch, o: TruckBarrier["obb"], h: number, color: string, pad = 0.02, yBase = 0): void {
  b.boxBase(o.halfWidth * 2 + pad * 2, h, o.halfLength * 2 + pad * 2, color, o.cx, yBase, o.cz, o.heading);
}

/** Hazard-striped pit barrier (yellow / black blocks) along an oriented box. */
function hazardBarrier(b: PartBatch, o: TruckBarrier["obb"], h: number): void {
  const along = o.halfLength >= o.halfWidth;
  const len = (along ? o.halfLength : o.halfWidth) * 2;
  const thick = (along ? o.halfWidth : o.halfLength) * 2 + 0.04;
  const n = Math.max(1, Math.round(len / 0.6));
  const seg = len / n;
  const { fx, fz, rx, rz } = axes(o.heading);
  for (let i = 0; i < n; i++) {
    const t = -len / 2 + seg * (i + 0.5);
    const cx = o.cx + (along ? fx : rx) * t;
    const cz = o.cz + (along ? fz : rz) * t;
    const w = along ? thick : seg + 0.002;
    const d = along ? seg + 0.002 : thick;
    b.boxBase(w, h, d, i % 2 === 0 ? HAZARD_Y : HAZARD_K, cx, 0, cz, o.heading);
  }
  // A grey cap rail on top.
  b.boxBase((along ? thick : len) + 0.02, 0.06, (along ? len : thick) + 0.02, "#8E9196", o.cx, h, o.cz, o.heading);
}

/** Concrete trackside barrier with a fence on top (posts and rails), along an oriented box. */
function concreteWall(b: PartBatch, o: TruckBarrier["obb"], fence: boolean): void {
  barrierBox(b, o, 1.15, CONCRETE);
  // Painted top band in alternating red / white blocks (an arena barrier look).
  const along = o.halfLength >= o.halfWidth;
  const len = (along ? o.halfLength : o.halfWidth) * 2;
  const thick = (along ? o.halfWidth : o.halfLength) * 2 + 0.06;
  const n = Math.max(1, Math.round(len / 2));
  const seg = len / n;
  const { fx, fz, rx, rz } = axes(o.heading);
  for (let i = 0; i < n; i++) {
    const t = -len / 2 + seg * (i + 0.5);
    const cx = o.cx + (along ? fx : rx) * t;
    const cz = o.cz + (along ? fz : rz) * t;
    b.boxBase(along ? thick : seg, 0.16, along ? seg : thick, i % 2 === 0 ? "#D23A2E" : "#F4F1EA", cx, 0.95, cz, o.heading);
  }
  if (!fence) return;
  const posts = Math.max(2, Math.round(len / 3) + 1);
  for (let i = 0; i < posts; i++) {
    const t = -len / 2 + (len * i) / (posts - 1);
    b.boxBase(0.08, 1.45, 0.08, "#6B6F76", o.cx + (along ? fx : rx) * t, 1.15, o.cz + (along ? fz : rz) * t, o.heading);
  }
  for (const yy of [1.85, 2.55]) b.boxBase(along ? 0.06 : len, 0.06, along ? len : 0.06, "#7B7F86", o.cx, yy, o.cz, o.heading);
}

/** The box of a wall `t` thick whose face runs from a to b, its body on the LEFT of a->b. */
function faceWall(a: { x: number; z: number }, c: { x: number; z: number }, t: number): TruckBarrier["obb"] {
  const dx = c.x - a.x;
  const dz = c.z - a.z;
  const heading = Math.atan2(dx, dz);
  const { rx, rz } = axes(heading);
  return { cx: (a.x + c.x) / 2 - rx * (t / 2), cz: (a.z + c.z) / 2 - rz * (t / 2), halfWidth: t / 2, halfLength: Math.hypot(dx, dz) / 2, heading };
}

/** A ramp feature's geometry: plank slopes in alternating boards, dark sides, a yellow lip stripe. */
function rampParts(scene: Scene, b: PartBatch, r: TruckRamp): void {
  const R = r.rect;
  const along = r.dir.x !== 0;
  const width = along ? R.maxZ - R.minZ : R.maxX - R.minX;
  const length = along ? R.maxX - R.minX : R.maxZ - R.minZ;
  // Local frame: u along dir from the start edge, v across (-width/2..width/2). World point of (u, v, y).
  const start = { x: r.dir.x > 0 ? R.minX : r.dir.x < 0 ? R.maxX : (R.minX + R.maxX) / 2, z: r.dir.z > 0 ? R.minZ : r.dir.z < 0 ? R.maxZ : (R.minZ + R.maxZ) / 2 };
  const side = { x: -r.dir.z, z: r.dir.x };
  const P = (u: number, v: number, y: number): Xyz => ({ x: start.x + r.dir.x * u + side.x * v, y, z: start.z + r.dir.z * u + side.z * v });
  const h = (u: number) => rampHeightAt(r, start.x + r.dir.x * Math.min(u, length - 1e-6) + side.x * 0, start.z + r.dir.z * Math.min(u, length - 1e-6) + side.z * 0);
  const steps = r.kind === "pile" ? 12 : 1;
  const segments: [number, number][] = [];
  if (r.kind === "pile") {
    for (let i = 0; i < steps; i++) segments.push([(length * i) / steps, (length * (i + 1)) / steps]);
  } else {
    segments.push([0, r.up]);
    if (r.top > 0) segments.push([r.up, r.up + r.top]);
    if (r.down > 0) segments.push([r.up + r.top, r.up + r.top + r.down]);
  }
  const boards = r.kind === "pile" ? 1 : Math.round(width / 1.0);
  for (const [u0, u1] of segments) {
    const h0 = r.kind === "kicker" || u0 === 0 ? (u0 === 0 ? 0 : h(u0)) : h(u0 + 1e-6);
    const h1 = h(u1 - 1e-6);
    for (let k = 0; k < boards; k++) {
      const v0 = -width / 2 + (width * k) / boards;
      const v1 = -width / 2 + (width * (k + 1)) / boards;
      const top = r.kind === "pile" ? "#6E4C2C" : k % 2 === 0 ? PLANK : PLANK_ALT;
      const verts: Xyz[] = [P(u0, v0, 0), P(u1, v0, 0), P(u1, v1, 0), P(u0, v1, 0), P(u0, v0, Math.max(h0, 0.001)), P(u1, v0, Math.max(h1, 0.001)), P(u1, v1, Math.max(h1, 0.001)), P(u0, v1, Math.max(h0, 0.001))];
      const m = polyMesh(scene, `ramp_${r.id}`, verts, [
        [0, 1, 2, 3],
        [4, 5, 6, 7],
        [0, 1, 5, 4],
        [3, 2, 6, 7],
        [0, 3, 7, 4],
        [1, 2, 6, 5],
      ], top);
      b.add(m);
    }
    if (r.kind !== "pile") {
      // Dark side skirts (visible from the walls' side) as thin boxes.
      for (const v of [-width / 2, width / 2]) {
        const verts: Xyz[] = [P(u0, v - 0.05, 0), P(u1, v - 0.05, 0), P(u1, v + 0.05, 0), P(u0, v + 0.05, 0), P(u0, v - 0.05, h0 + 0.05), P(u1, v - 0.05, h1 + 0.05), P(u1, v + 0.05, h1 + 0.05), P(u0, v + 0.05, h0 + 0.05)];
        b.add(polyMesh(scene, `ramp_${r.id}_skirt`, verts, [[0, 1, 2, 3], [4, 5, 6, 7], [0, 1, 5, 4], [3, 2, 6, 7], [0, 3, 7, 4], [1, 2, 6, 5]], RAMP_SIDE));
      }
    }
  }
  if (r.kind !== "pile") {
    // Yellow / black stripe along the lip.
    const n = 16;
    for (let i = 0; i < n; i++) {
      const v0 = -width / 2 + (width * i) / n;
      const v1 = -width / 2 + (width * (i + 1)) / n;
      const c = P(r.up - 0.15, (v0 + v1) / 2, r.lipHeight);
      b.box(along ? 0.3 : v1 - v0, 0.05, along ? v1 - v0 : 0.3, i % 2 === 0 ? HAZARD_Y : HAZARD_K, c.x, c.y + 0.02, c.z);
    }
    if (r.kind === "kicker") {
      // The vertical back face, dark timber.
      const c = P(r.up + 0.06, 0, r.lipHeight / 2);
      b.box(along ? 0.12 : width, r.lipHeight, along ? width : 0.12, RAMP_SIDE, c.x, c.y, c.z);
    }
  }
}

/** Crushed cars on the pile: flattened bodies in faded colours resting on the hump. */
function crushedCars(scene: Scene, b: PartBatch, r: TruckRamp): void {
  const rnd = mulberry32(99);
  const colors = ["#7C8A99", "#9B3B32", "#3E5F8A", "#C9B458", "#5B7553", "#8B8F94", "#B5651D", "#4A4A55"];
  const R = r.rect;
  for (let i = 0; i < 16; i++) {
    const x = R.minX + 1.5 + rnd() * (R.maxX - R.minX - 3);
    const z = R.minZ + 1.2 + rnd() * (R.maxZ - R.minZ - 2.4);
    const y = rampHeightAt(r, x, z);
    const yaw = rnd() * Math.PI;
    const tilt = (rnd() - 0.5) * 0.25;
    const color = colors[Math.floor(rnd() * colors.length)]!;
    const car = box(scene, "crushed", 3.6, 0.45, 1.7, color, { pos: { x, y: Math.max(0.2, y - 0.05), z }, rot: { y: yaw, z: tilt } });
    b.add(car);
    const roof = box(scene, "crushedRoof", 1.6, 0.2, 1.5, color, { pos: { x: x + Math.sin(yaw) * 0.2, y: Math.max(0.2, y - 0.05) + 0.3, z: z + Math.cos(yaw) * 0.2 }, rot: { y: yaw, z: tilt * 1.5 } });
    b.add(roof);
    const glass = box(scene, "crushedGlass", 1.2, 0.08, 1.52, "#2A3440", { pos: { x: x + Math.sin(yaw) * 0.2, y: Math.max(0.2, y - 0.05) + 0.42, z: z + Math.cos(yaw) * 0.2 }, rot: { y: yaw, z: tilt * 1.5 } });
    b.add(glass);
  }
}

/** Stepped grandstand rows with coloured seats, the rows rising away from the track along `back` (unit). */
function stand(b: PartBatch, x0: number, x1: number, zFront: number, depth: number, rows: number, alongX: boolean, backSign: 1 | -1): void {
  const rowDepth = depth / rows;
  const seatColors = TRUCK_PAINTS.map((p) => p.body);
  for (let k = 0; k < rows; k++) {
    const h = 0.55 + k * 0.5;
    const near = zFront + backSign * k * rowDepth;
    const far = zFront + backSign * depth;
    const c0 = Math.min(near, far);
    const c1 = Math.max(near, far);
    const len = x1 - x0;
    if (alongX) b.boxBase(len, h, c1 - c0, k % 2 === 0 ? "#8A8F98" : "#9AA0A9", (x0 + x1) / 2, 0, (c0 + c1) / 2);
    else b.boxBase(c1 - c0, h, len, k % 2 === 0 ? "#8A8F98" : "#9AA0A9", (c0 + c1) / 2, 0, (x0 + x1) / 2);
    const seats = Math.floor(len / 0.62);
    for (let i = 0; i < seats; i++) {
      const t = x0 + 0.31 + i * 0.62;
      const color = seatColors[Math.floor(i / 14 + k) % seatColors.length]!;
      const sz = near + backSign * 0.3;
      if (alongX) b.boxBase(0.48, 0.32, 0.36, color, t, h, sz);
      else b.boxBase(0.36, 0.32, 0.48, color, sz, h, t);
    }
  }
}

/**
 * Everything static in the arena: the dirt (a painted ground texture), the infield, walls and tyre barriers, the pit
 * (bays, piers, hazard rails, bay numbers), the ramps and the crushed-car pile, the mud pit, the start / finish gantry
 * with its five lights, checkpoint flags, grandstands and light towers. Merged into a few meshes.
 */
export function buildArenaStatic(scene: Scene, parent: TransformNode, vertexMaterial: Material): ArenaStatic {
  const owned: { dispose(): void }[] = [];
  const batch = new PartBatch();
  const decals = new PartBatch();

  // Dirt ground with its painted markings.
  const dirtTex: DynamicTexture = makeDynamicTexture(scene, "truckDirt", DIRT_TEXTURE.width, DIRT_TEXTURE.height, { mips: true });
  paintDirt(dirtTex);
  const dirtMat = new StandardMaterial("truckDirtMat", scene);
  dirtMat.diffuseTexture = dirtTex;
  dirtMat.specularColor = new Color3(0.02, 0.02, 0.02);
  owned.push(dirtTex, dirtMat);
  const dw = DIRT_RECT.maxX - DIRT_RECT.minX;
  const dd = DIRT_RECT.maxZ - DIRT_RECT.minZ;
  const dirt = CreateGround("truckDirt", { width: dw, height: dd, subdivisions: 1 }, scene);
  dirt.position.set((DIRT_RECT.minX + DIRT_RECT.maxX) / 2, 0.02, (DIRT_RECT.minZ + DIRT_RECT.maxZ) / 2);
  dirt.material = dirtMat;
  dirt.receiveShadows = true;
  dirt.isPickable = false;
  dirt.parent = parent;
  dirt.metadata = { walkable: false };

  // Concrete over the room's floor (the walkway, the pit, the strips behind the walls), with expansion joints on the walkway.
  const room = TRUCK_ARENA_BOUNDS;
  decals.boxBase(room.maxX - room.minX, 0.006, room.maxZ - room.minZ, "#BDB8AE", (room.minX + room.maxX) / 2, 0, (room.minZ + room.maxZ) / 2);
  for (let x = room.minX + 5; x < room.maxX; x += 5) decals.boxBase(0.05, 0.008, PIT.barrierSouthZ - room.minZ, "#A5A097", x, 0, (room.minZ + PIT.barrierSouthZ) / 2);
  for (let z = room.minZ + 5; z < PIT.barrierSouthZ; z += 5) decals.boxBase(room.maxX - room.minX, 0.008, 0.05, "#A5A097", 0, 0, z);

  // Mud pit: glossy dark mud just above the dirt.
  const mudMat = new StandardMaterial("truckMudMat", scene);
  mudMat.diffuseColor = Color3.FromHexString("#4A2E17");
  mudMat.specularColor = new Color3(0.35, 0.3, 0.25);
  mudMat.specularPower = 48;
  owned.push(mudMat);
  const mudMeshes: Mesh[] = [];
  for (const m of TRUCK_MUD) {
    const mesh = CreateGround(`truckMud_${m.id}`, { width: m.rect.maxX - m.rect.minX, height: m.rect.maxZ - m.rect.minZ }, scene);
    mesh.position.set((m.rect.minX + m.rect.maxX) / 2, 0.045, (m.rect.minZ + m.rect.maxZ) / 2);
    mesh.material = mudMat;
    mesh.receiveShadows = true;
    mesh.isPickable = false;
    mesh.parent = parent;
    mudMeshes.push(mesh);
    // A few puddle highlights.
    const rnd = mulberry32(5);
    for (let i = 0; i < 9; i++) {
      const px = m.rect.minX + 1 + rnd() * (m.rect.maxX - m.rect.minX - 2);
      const pz = m.rect.minZ + 1 + rnd() * (m.rect.maxZ - m.rect.minZ - 2);
      decals.box(1.2 + rnd() * 2.2, 0.01, 0.8 + rnd() * 1.6, "#5E3D20", px, 0.055, pz, rnd() * Math.PI);
    }
  }

  // Infield: a grass island inside the tyre walls, with a scoreboard tower.
  const isl = TRACK.island;
  const k = TRACK.islandChamfer;
  const inset = 0.8;
  decals.boxBase(isl.maxX - isl.minX - 2 * inset, 0.08, isl.maxZ - isl.minZ - 2 * k, "#5E8F3E", (isl.minX + isl.maxX) / 2, 0, (isl.minZ + isl.maxZ) / 2);
  decals.boxBase(isl.maxX - isl.minX - 2 * k, 0.08, isl.maxZ - isl.minZ - 2 * inset, "#5E8F3E", (isl.minX + isl.maxX) / 2, 0, (isl.minZ + isl.maxZ) / 2);
  for (const [cx, cz, yaw] of [
    [isl.minX + k / 2 + 0.4, isl.minZ + k / 2 + 0.4, Math.PI / 4],
    [isl.maxX - k / 2 - 0.4, isl.minZ + k / 2 + 0.4, -Math.PI / 4],
    [isl.maxX - k / 2 - 0.4, isl.maxZ - k / 2 - 0.4, Math.PI / 4],
    [isl.minX + k / 2 + 0.4, isl.maxZ - k / 2 - 0.4, -Math.PI / 4],
  ] as const) {
    decals.boxBase(k * 0.9, 0.08, k * 0.9, "#5E8F3E", cx, 0, cz, yaw);
  }
  // Mown stripes.
  for (let i = 0; i < 10; i++) {
    const x0 = isl.minX + k + i * ((isl.maxX - isl.minX - 2 * k) / 10);
    if (i % 2 === 0) decals.boxBase((isl.maxX - isl.minX - 2 * k) / 10, 0.081, isl.maxZ - isl.minZ - 2 * inset - 0.2, "#6A9C47", x0 + (isl.maxX - isl.minX - 2 * k) / 20, 0, (isl.minZ + isl.maxZ) / 2);
  }
  // Scoreboard tower in the middle of the infield, its screen facing the pit.
  batch.boxBase(0.6, 7, 0.6, "#4B5260", -4, 0, 8);
  batch.boxBase(0.6, 7, 0.6, "#4B5260", 4, 0, 8);
  batch.boxBase(12, 5, 0.8, "#1B2A44", 0, 6.5, 8);
  batch.boxBase(11.2, 4.2, 0.1, "#0E1626", 0, 6.9, 7.56);
  batch.boxBase(12.4, 0.5, 1.0, "#F2C230", 0, 11.5, 8);
  // Screen art (original): three chevrons and a checkered band.
  for (let i = 0; i < 3; i++) batch.box(1.6, 1.6, 0.06, TRUCK_PAINTS[i]!.body, -3 + i * 3, 9, 7.48, 0);
  for (let i = 0; i < 14; i++) batch.box(0.8, 0.4, 0.06, i % 2 === 0 ? "#F7F7F2" : "#1C1C1C", -5.2 + i * 0.8, 7.6, 7.48);

  // Walls: outer jersey walls (north, east, west), corner chamfers, the trackside barrier (with a fence) and the pit.
  const T = TRACK;
  const s = PIT.trackEdgeZ;
  const c = T.outerChamfer;
  concreteWall(batch, faceWall({ x: T.outerMinX + c, z: T.outerMaxZ }, { x: T.outerMaxX - c, z: T.outerMaxZ }, 0.6), true);
  concreteWall(batch, faceWall({ x: T.outerMaxX, z: T.outerMaxZ - c }, { x: T.outerMaxX, z: s + c }, 0.6), true);
  concreteWall(batch, faceWall({ x: T.outerMinX, z: s + c }, { x: T.outerMinX, z: T.outerMaxZ - c }, 0.6), true);
  const tyreSpots: { x: number; y: number; z: number; yaw: number; scale: number }[] = [];
  const paintedSpots: typeof tyreSpots = [];
  for (const bar of TRUCK_BARRIERS) {
    if (/^outer_(sw|se|ne|nw)$/.test(bar.id)) {
      // Corner chamfers (the straight outer walls are drawn above at their faces: their colliders reach the room walls).
      concreteWall(batch, bar.obb, true);
    } else if (bar.id === "venue_pit_track_wall_w" || bar.id === "venue_pit_track_wall_e" || bar.id.startsWith("venue_pit_cap_")) {
      concreteWall(batch, bar.obb, true);
    } else if (bar.id.startsWith("venue_pit_rail") || bar.id.startsWith("venue_pit_wall")) {
      hazardBarrier(batch, bar.obb, 1.0);
    } else if (bar.kind === "tyres") {
      const o = bar.obb;
      const { fx, fz } = axes(o.heading);
      const n = Math.max(1, Math.floor((o.halfLength * 2) / 0.9));
      for (let i = 0; i < n; i++) {
        const t = -o.halfLength + 0.45 + i * ((o.halfLength * 2 - 0.9) / Math.max(1, n - 1));
        for (let lvl = 0; lvl < 4; lvl++) {
          const spot = { x: o.cx + fx * t, y: 0.15 + lvl * 0.29, z: o.cz + fz * t, yaw: i * 0.7, scale: 1 };
          if (lvl === 3 && i % 2 === 0) paintedSpots.push(spot);
          else tyreSpots.push(spot);
        }
      }
    }
  }

  // Pit: concrete bay floors, boarding mats on the piers, bay number boards on the pit wall.
  const numberMats: StandardMaterial[] = [];
  const numberMeshes: Mesh[] = [];
  TRUCK_FLEET.forEach((t, i) => {
    const bay = baySpan(i);
    decals.boxBase(bay.maxX - bay.minX, 0.03, PIT.trackEdgeZ - PIT.wallNorthZ, "#B4B0A8", (bay.minX + bay.maxX) / 2, 0, (PIT.wallNorthZ + PIT.trackEdgeZ) / 2);
    // Parking box lines.
    decals.boxBase(0.12, 0.035, PIT.trackEdgeZ - PIT.wallNorthZ - 0.4, "#F4F1EA", bay.minX + 0.2, 0, (PIT.wallNorthZ + PIT.trackEdgeZ) / 2);
    decals.boxBase(0.12, 0.035, PIT.trackEdgeZ - PIT.wallNorthZ - 0.4, "#F4F1EA", bay.maxX - 0.2, 0, (PIT.wallNorthZ + PIT.trackEdgeZ) / 2);
    // Boarding mat (yellow with a dark border) where the driver stands.
    decals.boxBase(1.36, 0.012, 2.6, "#2B2B2B", t.boarding.x, 0, t.boarding.z);
    decals.boxBase(1.16, 0.016, 2.4, HAZARD_Y, t.boarding.x, 0, t.boarding.z);
    const paint = TRUCK_PAINTS[t.paint % TRUCK_PAINTS.length]!;
    const tex = numberTexture(scene, t.number, paint);
    const mat = new StandardMaterial(`truckBayNum${t.number}`, scene);
    mat.diffuseTexture = tex;
    mat.useAlphaFromDiffuseTexture = true;
    mat.emissiveColor = new Color3(0.3, 0.3, 0.3);
    numberMats.push(mat);
    owned.push(tex, mat);
    // Board on a post at the pier's pit-wall end, facing the walkway (south) and the boarding mat.
    const bx = (bay.minX + bay.maxX) / 2;
    batch.boxBase(1.2, 1.2, 0.08, paint.body, bx, 1.0, PIT.wallSouthZ - 0.06);
    const plane = CreatePlane(`truckBayNumPlane${t.number}`, { size: 1.0 }, scene);
    plane.position.set(bx, 1.6, PIT.wallSouthZ - 0.11);
    plane.material = mat;
    plane.isPickable = false;
    plane.parent = parent;
    numberMeshes.push(plane);
  });

  // Ramps, the table top, the kickers and the crushed-car pile.
  for (const r of TRUCK_RAMPS) {
    rampParts(scene, r.kind === "pile" ? decals : batch, r);
    if (r.kind === "pile") crushedCars(scene, batch, r);
  }

  // Start / finish gantry over the line, five lights facing the grid (west).
  const gx = START_LINE_X;
  const zA = PIT.trackEdgeZ - 0.2;
  const zB = TRACK.island.minZ + 0.4;
  for (const z of [zA, zB]) batch.boxBase(0.6, 7.2, 0.6, "#3A3F48", gx, 0, z);
  batch.boxBase(0.9, 1.1, zB - zA + 0.6, "#2A2E35", gx, 6.4, (zA + zB) / 2);
  // Chequered banner on both faces.
  const cells = 20;
  for (let i = 0; i < cells; i++) {
    for (let row = 0; row < 2; row++) {
      const color = (i + row) % 2 === 0 ? "#F7F7F2" : "#1C1C1C";
      const z = zA + ((zB - zA) * (i + 0.5)) / cells;
      batch.box(0.94, 0.42, (zB - zA) / cells, color, gx, 6.62 + row * 0.42, z);
    }
  }
  batch.boxBase(0.5, 0.9, 3.6, "#15171B", gx - 0.6, 5.3, (zA + zB) / 2);
  const startLights: Mesh[] = [];
  for (let i = 0; i < 5; i++) {
    const lamp = applyPart(CreateTorus(`truckStartLight${i}`, { diameter: 0.34, thickness: 0.28, tessellation: 12 }, scene), "#FFFFFF", { pos: { x: gx - 0.86, y: 5.75, z: (zA + zB) / 2 - 1.4 + i * 0.7 }, rot: { z: Math.PI / 2 } });
    lamp.parent = parent;
    startLights.push(lamp);
  }

  // Checkpoint flags: a post with a blue flag at both ends of every checkpoint line.
  for (const cp of TRUCK_CHECKPOINTS) {
    if (cp.index === 0) continue;
    for (const p of [cp.a, cp.b]) {
      batch.boxBase(0.12, 3.6, 0.12, "#E9E6DF", p.x, 1.1, p.z);
      batch.boxBase(0.06, 0.8, 1.1, "#2D6CDF", p.x, 3.7, p.z + 0.55);
      batch.boxBase(0.065, 0.24, 1.1, "#F4F1EA", p.x, 3.98, p.z + 0.55);
    }
  }

  // Grandstands behind the north wall (with a back wall for the arena sign and a canopy) and along the side walls.
  const B = TRUCK_ARENA_BOUNDS;
  stand(batch, -72, 72, T.outerMaxZ + 0.6, B.maxZ - 0.6 - (T.outerMaxZ + 0.6), 4, true, 1);
  batch.boxBase(150, 8, 0.4, "#1F3B73", 0, 0, B.maxZ - 0.35);
  batch.boxBase(150, 0.25, 4.2, "#2A4A8A", 0, 8, B.maxZ - 2.1);
  for (let x = -70; x <= 70; x += 14) batch.boxBase(0.25, 8, 0.25, "#3A3F48", x, 0, T.outerMaxZ + 0.75);
  stand(batch, -30, 40, T.outerMaxX + 0.65, B.maxX - 0.1 - (T.outerMaxX + 0.65), 3, false, 1);
  stand(batch, -30, 40, T.outerMinX - 0.65, B.maxX - 0.1 - (T.outerMaxX + 0.65), 3, false, -1);

  // Light towers in the corners behind the stands.
  for (const [x, z] of [
    [B.minX + 1.2, B.maxZ - 1.2],
    [B.maxX - 1.2, B.maxZ - 1.2],
    [B.minX + 1.2, -20],
    [B.maxX - 1.2, -20],
  ] as const) {
    batch.boxBase(0.5, 15, 0.5, "#5A606A", x, 0, z);
    batch.boxBase(3.2, 1.4, 0.5, "#2A2E35", x, 15, z);
    for (let i = 0; i < 4; i++) batch.box(0.6, 0.45, 0.08, "#FFF6C8", x - 1.1 + i * 0.73, 15.7, z + (z > 0 ? -0.29 : 0.29));
  }

  // Tyres: one thin-instanced torus for the black stacks, one for the painted (white) top tyres.
  const tyreMat = new StandardMaterial("truckTyreMat", scene);
  tyreMat.diffuseColor = Color3.FromHexString("#1F1F21");
  tyreMat.specularColor = new Color3(0.05, 0.05, 0.05);
  const paintedMat = new StandardMaterial("truckTyrePaintMat", scene);
  paintedMat.diffuseColor = Color3.FromHexString("#ECEAE4");
  paintedMat.specularColor = new Color3(0.05, 0.05, 0.05);
  owned.push(tyreMat, paintedMat);
  const tyres = CreateTorus("truckTyres", { diameter: 0.6, thickness: 0.3, tessellation: 10 }, scene);
  tyres.material = tyreMat;
  tyres.isPickable = false;
  tyres.parent = parent;
  tyres.receiveShadows = true;
  setThinInstances(tyres, thinInstanceMatrices(tyreSpots));
  const painted = CreateTorus("truckTyresPainted", { diameter: 0.6, thickness: 0.3, tessellation: 10 }, scene);
  painted.material = paintedMat;
  painted.isPickable = false;
  painted.parent = parent;
  painted.receiveShadows = true;
  setThinInstances(painted, thinInstanceMatrices(paintedSpots));

  const merged = batch.build("truckArenaStatic", scene);
  const flat = decals.build("truckArenaDecals", scene);
  const casters: Mesh[] = [tyres, painted];
  for (const m of [merged, flat]) {
    if (!m) continue;
    m.material = vertexMaterial;
    m.receiveShadows = true;
    m.parent = parent;
    m.isPickable = false;
    m.freezeWorldMatrix();
  }
  if (merged) casters.push(merged);
  if (flat) casters.push(flat);
  const meshes = [dirt, ...mudMeshes, tyres, painted, ...numberMeshes, ...startLights, ...(merged ? [merged] : []), ...(flat ? [flat] : [])];
  return {
    casters,
    startLights,
    dispose() {
      for (const m of meshes) m.dispose();
      for (const o of owned) o.dispose();
    },
  };
}
